import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { TaskStatus } from "@api/types";
import { getCustomerName } from "@lib/utils";
import { AlarmClock, CalendarClock, CheckCircle2, Circle, ListTodo } from "lucide-react";
import { DateTime } from "luxon";
import { type ReactNode, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { CustomerAvatar } from "@/modules/customer/customer-avatar";
import {
  descriptionSnippet,
  dueDateLabel,
  getUserName,
  isTaskOverdue,
  type PopulatedTask,
  snoozedUntilLabel,
  TaskUserAvatar,
} from "./shared";
import { TASK_STATUS_DOTS } from "./status-badge";

const COLUMNS: { status: TaskStatus; label: string }[] = [
  { status: "snoozed", label: "Snoozed" },
  { status: "todo", label: "To do" },
  { status: "in_progress", label: "In progress" },
  { status: "completed", label: "Done" },
];

interface TasksBoardProps {
  tasks?: PopulatedTask[];
  isLoading?: boolean;
  selectedId?: string;
  emptyState?: ReactNode;
  onSelect: (task: PopulatedTask) => void;
  onUpdate: (task: PopulatedTask, body: Record<string, unknown>) => void;
}

interface TaskCardProps {
  task: PopulatedTask;
  selected?: boolean;
  onSelect?: (task: PopulatedTask) => void;
  onUpdate?: (task: PopulatedTask, body: Record<string, unknown>) => void;
  /** Hide the assignee footer (e.g. "My tasks" where it's implied). */
  showAssignee?: boolean;
  /** Static render inside the DragOverlay (no dnd bindings). */
  overlay?: boolean;
  /** Draggable bindings from the board; a plain clickable card when omitted. */
  dnd?: {
    ref: (el: HTMLElement | null) => void;
    listeners?: DraggableSyntheticListeners;
    attributes?: DraggableAttributes;
    isDragging: boolean;
  };
}

/**
 * The task card shared by the board and the inbox "My tasks" column. Only
 * the board passes `dnd` bindings; elsewhere it's a plain clickable card.
 */
export function TaskCard({
  task,
  selected,
  onSelect,
  onUpdate,
  showAssignee = true,
  overlay,
  dnd,
}: TaskCardProps) {
  const overdue = isTaskOverdue(task);
  const completed = task.status === "completed";

  return (
    <div
      ref={dnd?.ref}
      {...(dnd ? { ...dnd.listeners, ...dnd.attributes } : {})}
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(task)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onSelect?.(task);
      }}
      className={cn(
        "bg-background text-card-foreground block w-full rounded-lg border border-border p-3 text-left shadow-sm transition-colors dark:border-subtle",
        overlay
          ? "cursor-grabbing shadow-lg"
          : cn(
            dnd ? "cursor-grab" : "cursor-pointer",
            "hover:border-foreground/40 hover:bg-muted/50",
          ),
        dnd?.isDragging && "opacity-40",
        selected && "border-ring dark:border-ring hover:border-ring",
      )}
    >
      <div className="flex items-start gap-2.5">
        {onUpdate && (
          <button
            type="button"
            aria-label={completed ? "Reopen task" : "Complete task"}
            className="group/check mt-0.5 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onUpdate(task, { status: completed ? "todo" : "completed" });
            }}
          >
            {completed ? (
              <CheckCircle2 className="size-[18px] text-green-500 dark:text-green-400" />
            ) : (
              <>
                <Circle className="size-[18px] text-muted-foreground/40 group-hover/check:hidden" />
                <CheckCircle2 className="hidden size-[18px] text-green-500 group-hover/check:block dark:text-green-400" />
              </>
            )}
          </button>
        )}
        {/* The card's grab cursor signals dragging; the text signals "click
            to open" with a pointer instead. */}
        <div className={cn("min-w-0 flex-1", !overlay && "cursor-pointer")}>
          <span
            className={cn(
              "block text-sm font-medium leading-snug text-foreground",
              completed && "text-muted-foreground line-through",
            )}
          >
            {task.title}
          </span>
          {task.description ? (
            <span className="mt-0.5 block truncate text-xs text-muted-foreground/80">
              {descriptionSnippet(task.description)}
            </span>
          ) : null}
        </div>
      </div>

      {task.tags.length > 0 && (
        <span className="mt-2 flex flex-wrap gap-1">
          {task.tags.slice(0, 3).map((tag) => (
            <Badge key={tag} variant="secondary" className="px-1.5 py-0 text-[11px] font-normal">
              {tag}
            </Badge>
          ))}
        </span>
      )}

      {(task.dueDate || task.snoozedUntil) && (
        <div className="mt-2 flex flex-col gap-1 text-xs text-muted-foreground">
          {task.status === "snoozed" && task.snoozedUntil ? (
            <span className="flex items-center gap-1.5">
              <AlarmClock className="size-3.5 shrink-0" aria-hidden="true" />
              {snoozedUntilLabel(task.snoozedUntil)}
            </span>
          ) : null}
          {task.dueDate ? (
            <span
              className={cn("flex items-center gap-1.5", overdue && !completed && "text-destructive")}
            >
              <CalendarClock className="size-3.5 shrink-0" aria-hidden="true" />
              {dueDateLabel(task.dueDate)}
            </span>
          ) : null}
        </div>
      )}

      {(task.customer || showAssignee) && (
        <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-border/60 pt-2">
          {task.customer ? (
            <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
              <CustomerAvatar customer={task.customer} className="size-4 shrink-0 text-[9px]" />
              <span className="truncate">{getCustomerName(task.customer)}</span>
            </span>
          ) : (
            <span className="text-xs text-muted-foreground/50">No contact</span>
          )}
          {showAssignee && (
            <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
              {task.user ? (
                <>
                  <TaskUserAvatar user={task.user} className="size-4" />
                  <span className="max-w-24 truncate">{getUserName(task.user)}</span>
                </>
              ) : (
                <span className="text-muted-foreground/50">Unassigned</span>
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** Board-only wrapper that binds the card to dnd-kit dragging. */
function DraggableTaskCard(props: Omit<TaskCardProps, "dnd" | "overlay">) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: props.task.id,
    data: { task: props.task },
  });

  return (
    <TaskCard {...props} dnd={{ ref: setNodeRef, listeners, attributes, isDragging }} />
  );
}

function BoardColumn({
  status,
  label,
  tasks,
  selectedId,
  dragging,
  onSelect,
  onUpdate,
}: {
  status: TaskStatus;
  label: string;
  tasks: PopulatedTask[];
  selectedId?: string;
  /** A card is being dragged somewhere on the board. */
  dragging?: boolean;
  onSelect: (task: PopulatedTask) => void;
  onUpdate: (task: PopulatedTask, body: Record<string, unknown>) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });

  return (
    // Same swimlane recipe as the inbox kanban columns.
    <div
      className={cn(
        "bg-inset dark:bg-card flex h-full min-w-[85vw] max-w-[85vw] snap-start flex-col rounded-lg border p-2 pt-0 transition-colors sm:min-w-[20rem] sm:max-w-[20rem]",
        isOver ? "border-border" : "border-transparent dark:border-subtle",
      )}
    >
      <h2 className="bg-inset dark:bg-card text-foreground sticky top-0 z-10 flex shrink-0 items-center gap-2 px-1 pt-3 pb-3 text-base font-semibold">
        <span className={cn("size-2 rounded-full", TASK_STATUS_DOTS[status])} aria-hidden="true" />
        {label}
        {tasks.length > 0 && (
          <Badge variant="outline" className="bg-background">
            {tasks.length}
          </Badge>
        )}
      </h2>
      <div ref={setNodeRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex min-h-full flex-col gap-2">
          {tasks.map((task) => (
            <DraggableTaskCard
              key={task.id}
              task={task}
              selected={task.id === selectedId}
              onSelect={onSelect}
              onUpdate={onUpdate}
            />
          ))}
          {dragging ? (
            // Active drag: the remaining column height becomes an explicit
            // dropzone that lights up when the card is over this column.
            <div
              className={cn(
                "flex min-h-24 flex-1 flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-xs transition-colors",
                isOver
                  ? "border-foreground/40 bg-muted/60 text-muted-foreground"
                  : "border-border/60 text-muted-foreground/60",
              )}
            >
              <ListTodo className="size-5" strokeWidth={1.5} aria-hidden="true" />
              Drop here
            </div>
          ) : tasks.length === 0 ? (
            // Same empty treatment as the inbox kanban columns.
            <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 text-sm">
              <ListTodo className="w-10 h-10" strokeWidth={1} aria-hidden="true" />
              No tasks
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function TasksBoard(props: TasksBoardProps) {
  const [activeTask, setActiveTask] = useState<PopulatedTask | null>(null);

  // Distance activation keeps plain clicks working (open the drawer) while
  // a small drag starts a card move.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const byStatus = useMemo(() => {
    const groups: Record<TaskStatus, PopulatedTask[]> = {
      todo: [],
      in_progress: [],
      snoozed: [],
      completed: [],
    };
    for (const task of props.tasks ?? []) {
      groups[task.status]?.push(task);
    }
    // Open columns: soonest due first (undated last); Done: most recent first.
    for (const column of COLUMNS) {
      groups[column.status] = groups[column.status].toSorted((a, b) => {
        if (column.status === "completed") {
          return (b.completedAt ?? b.updatedAt).localeCompare(a.completedAt ?? a.updatedAt);
        }
        if (!a.dueDate && !b.dueDate) return b.createdAt.localeCompare(a.createdAt);
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return a.dueDate.localeCompare(b.dueDate);
      });
    }
    return groups;
  }, [props.tasks]);

  function handleDragStart(event: DragStartEvent) {
    setActiveTask((event.active.data.current?.task as PopulatedTask) ?? null);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveTask(null);
    const task = event.active.data.current?.task as PopulatedTask | undefined;
    const status = event.over?.id as TaskStatus | undefined;
    if (!task || !status || status === task.status) return;

    if (status === "snoozed") {
      // Snoozing needs a wake date; default to tomorrow morning. The drawer
      // offers finer control.
      const until = DateTime.now()
        .plus({ days: 1 })
        .set({ hour: 9, minute: 0, second: 0, millisecond: 0 });
      props.onUpdate(task, { status, snoozedUntil: until.toJSDate() });
      return;
    }
    props.onUpdate(task, { status });
  }

  if (props.isLoading && !props.tasks) {
    return (
      <div className="flex h-full gap-4 px-4 py-4 md:gap-6 md:px-6">
        {COLUMNS.map((column) => (
          <div
            key={column.status}
            className="bg-inset dark:bg-card flex shrink-0 flex-col gap-2 rounded-lg border border-transparent p-2 pt-3 dark:border-subtle sm:min-w-[20rem] sm:max-w-[20rem]"
          >
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  if (!props.tasks?.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-4 py-16 text-center">
        {props.emptyState}
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex h-full snap-x snap-mandatory flex-nowrap gap-4 overflow-x-auto scroll-px-4 px-4 py-4 sm:snap-none md:gap-6 md:px-6">
        {COLUMNS.map((column) => (
          <BoardColumn
            key={column.status}
            status={column.status}
            label={column.label}
            tasks={byStatus[column.status]}
            selectedId={props.selectedId}
            dragging={activeTask != null}
            onSelect={props.onSelect}
            onUpdate={props.onUpdate}
          />
        ))}
      </div>
      <DragOverlay>
        {activeTask ? (
          <div className="w-[20rem]">
            <TaskCard task={activeTask} overlay />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
